import { MediaItem } from '../../../app/services/mediaItems';

/**
 * MA（CSV シーケンス）機能の共通型定義。
 *
 * CSV でショット内のメディアアイテム（動画 / オーディオ）を時系列に並べ、
 * 動画セグメント単位で FE 側ミックス再生するためのデータモデル。
 * パース（parseMaCsv）→ リンク（linkMaSequence）→ 再生（useMaAudioEngine）の順で使う。
 */

/**
 * シーケンスの時刻値:
 * - frames: 整数（絶対フレーム）
 * - timecode: M:SS.FF タイムコード
 * - seconds: 秒の実数値（RPP 由来。fps 換算不要）
 */
export type MaTimeValue =
  | { kind: 'frames'; frames: number }
  | { kind: 'timecode'; minutes: number; seconds: number; frames: number | null }
  | { kind: 'seconds'; seconds: number };

/** CSV / RPP 1 行のパース結果（メディア解決前） */
export interface MaCsvRow {
  /** ソースファイル上の行番号（1 始まり・ヘッダー / コメント行含む実行番号） */
  line: number;
  /** media 列の生値（ファイル名 or メディアアイテム名） */
  media: string;
  start: MaTimeValue;
  end: MaTimeValue | null;
  /** 動画行のみ有効: 動画ファイル内の開始オフセット */
  offset: MaTimeValue | null;
  /**
   * オーディオ行のみ有効: 1 始まりのトラック番号、またはトラック名（文字列）。
   * 未指定（null）は自動配置。トラック名はリンク時に数値 ID へ採番される
   */
  track: number | string | null;
  /** オーディオ行のみ有効: クリップゲイン（dB） */
  gainDb: number | null;
  /**
   * クリップ表示色（#rrggbb）。RPP のマーカー / リージョン個別色、
   * または保存 CSV の color 列由来。未指定（undefined / null）は
   * リンク時にルーラーレーン色へフォールバックする
   */
  color?: string | null;
}

/**
 * RPP のルーラーレーン（REAPER 7 の RULERLANE）。
 * マーカーのトラック指定（レーン名 = トラック名）と、クリップ色の
 * フォールバック（レーン色）・トラックの表示順（index 昇順）の元になる。
 * 保存 CSV には `# lanes:` コメント行として書き出され、ラウンドトリップする
 */
export interface MaRulerLane {
  /** レーン番号（RULERLANE の index。表示順の正） */
  index: number;
  name: string;
  /** レーン色（#rrggbb。未設定は null） */
  colorHex: string | null;
}

/** パース / リンクの警告（行に紐づかないものは line: null） */
export interface MaWarning {
  line: number | null;
  message: string;
}

export interface MaParseResult {
  rows: MaCsvRow[];
  warnings: MaWarning[];
  /** `(x)` / `[x]` オプションスロット（RPP / 保存 CSV の両方から抽出。マーカーなしは空配列） */
  optionSlots: MaOptionSlotSource[];
  /**
   * ルーラーレーン一覧（index 昇順）。RPP の RULERLANE / 保存 CSV の
   * `# lanes:` コメント由来。レーンなし・旧 CSV は undefined
   */
  rulerLanes?: MaRulerLane[];
  /**
   * ビデオアイテムを持つ最初のトラックの名前（RPP の TRACK NAME /
   * 保存 CSV の `# videotrack:` コメント由来）。タイムラインの動画トラックの
   * ラベルに使う（未取得・無名は undefined → 'MOV' 表示）
   */
  videoTrackName?: string | null;
}

// ===== オプションスロット（`(x)` / `[x]` マーカー） =====
//
// `(x)` マーカー = オプション差し込みスロット（`(x=候補キー)` で初期選択を指定可）。
// 最初の `[` マーカー以降は「オプション候補置き場（パレット領域）」として
// 本編タイムラインから除外され、`[x]` セクション内で同じ開始位置に並ぶ
// 行群（SE + VC 等のバンドル）が 1 候補になる。
// 既定では初期選択（未指定は先頭候補）がスロット位置に配置され、
// タイムライン編集で切替できる。保存 CSV にも同じ構造が書き出されるため、
// 保存後のテイクでもオプション切替を維持できる。

/** オプション候補 1 つぶんのソース行（パース段階・メディア未解決） */
export interface MaOptionChoiceSource {
  /** スロット内で一意の候補キー */
  key: string;
  /** 表示ラベル（候補クラスタ先頭の名前・拡張子なし） */
  label: string;
  /** 候補クラスタ先頭を 0 とする相対時刻の行 */
  rows: MaCsvRow[];
}

/** `(x)` スロット 1 つぶんのソース（パース段階） */
export interface MaOptionSlotSource {
  /** スロットキー（マーカー名 + 配置位置で一意） */
  key: string;
  /** マーカー名（括弧の内側・初期選択部を除く） */
  name: string;
  /** シーケンス絶対時刻の配置位置 */
  start: MaTimeValue;
  /** `(x)` マーカーのソース行番号（警告用） */
  line: number;
  choices: MaOptionChoiceSource[];
  defaultChoiceKey: string;
}

/** セグメントに帰属したオーディオクリップ（時刻は秒に解決済み） */
export interface MaClip {
  audioItem: MediaItem;
  /**
   * ソース上の名前の生値（RPP リージョン名 / CSV の media 列。
   * 編集追加クリップは file_name の拡張子除去）。表示名 CSV の照合に使う
   */
  sourceName: string;
  /** セグメント相対の開始秒（動画 0F 基準） */
  startSeconds: number;
  /**
   * クリップ長（秒）。end 指定があれば end - start、なければ item.duration。
   * duration 未解析の場合は null（再生時はデコード実尺、表示時は 0 扱い）
   */
  durationSeconds: number | null;
  /** CSV で end が明示指定されていたか（CSV 再生成時に end 列を保持するため） */
  hasExplicitEnd: boolean;
  /**
   * 後着優先カット位置（セグメント相対秒）。同一トラック明示指定で
   * 後続クリップと重なる場合、後続クリップの開始位置で前のクリップを切る。
   * null = カットなし。実効長 = min(実尺, truncateAtSeconds - startSeconds)
   */
  truncateAtSeconds: number | null;
  /** 1 始まりのトラック番号（自動配置解決済み） */
  track: number;
  /** トラック名（CSV / RPP で文字列 track 指定時のみ。表示・CSV 再生成用） */
  trackName: string | null;
  gainDb: number;
  /** CSV 行番号（警告・デバッグ用。編集追加クリップは 1,000,000 以上の擬似値） */
  line: number;
  /** タイムライン編集中のクリップ識別子（編集モード時のみ設定） */
  editId?: string;
  /** オプションスロット由来のクリップ（候補切替時の差し替え対象判定用） */
  optionSlotKey?: string;
  /**
   * クリップ表示色（#rrggbb）。行の個別色またはルーラーレーン色の
   * フォールバック解決済み。未設定はトーン既定色で描画される
   */
  color?: string | null;
}

/** リンク済みオプション候補（クリップは帰属セグメント相対・メディア解決済み） */
export interface MaOptionChoice {
  key: string;
  label: string;
  /** 候補のクリップ（startSeconds はセグメント相対。editId なしのテンプレート） */
  clips: MaClip[];
  /**
   * 候補の差し替え動画（パレット領域の動画アイテム由来）。
   * この候補が選択中（既定含む）の間、スロット位置から先の表示動画を
   * 差し替える（videoSpans に区間として反映される）。
   * null / 未設定 = 動画なし候補（選択時は本編動画のまま）
   */
  videoItem?: MediaItem | null;
  /**
   * 候補動画の「スロット位置時点のファイル内時刻」（秒）。
   * RPP の SOFFS（+ クラスタ内相対配置の正規化）由来で、
   * REAPER で Region の下に見えるフレーム = スロット時刻のフレームを再現する。
   * videoItem があるときのみ意味を持つ（未設定は 0 = 頭から）
   */
  videoOffsetSeconds?: number;
  /**
   * 候補動画のコンテンツ尺（スロット位置起点の秒）。パレット上の配置
   * （クラスタ相対の end）由来で、本編動画より長い候補動画を選んだときに
   * セグメントの表示尺をここまで延長する（resolvedDisplaySeconds）。
   * videoItem があるときのみ意味を持つ（未設定 = 尺不明、延長しない）
   */
  videoDurationSeconds?: number;
}

/** リンク済みオプションスロット（`(x)` / `[x]` マーカー） */
export interface MaOptionSlot {
  key: string;
  /** マーカー名（UI 表示用） */
  name: string;
  /** 帰属する動画セグメント index */
  segmentIndex: number;
  /** セグメント相対の配置秒 */
  startSeconds: number;
  choices: MaOptionChoice[];
  defaultChoiceKey: string;
}

/**
 * セグメント内の動画表示区間。
 *
 * セグメントの動画は「1 本 + 固定オフセット」ではなく時間区間のリストで表す。
 * 先頭区間（startSeconds 0）は通常本編動画で、オプション候補の動画が
 * 選択されるとスロット位置から先が候補動画の区間になる（候補動画のファイルが
 * 先に尽きる場合は、その位置から本編の続きの区間に戻る）。
 * 表示時刻 t のファイル内時刻 = fileOffsetSeconds + (t - startSeconds)
 */
export interface MaVideoSpan {
  /** セグメント相対の区間開始秒（先頭区間は 0） */
  startSeconds: number;
  videoItem: MediaItem;
  /** 区間開始時点のファイル内時刻（秒） */
  fileOffsetSeconds: number;
  /**
   * この区間のコンテンツ尺（startSeconds 起点の秒）。オプション候補動画の
   * 区間のみ設定され、セグメント表示尺の延長（resolvedDisplaySeconds）に使う。
   * 未設定 = 本編区間（表示尺は videoDisplaySeconds が決める）
   */
  contentDurationSeconds?: number;
}

/** 表示時刻 t が属する区間 index（先頭区間より前は 0） */
export function videoSpanIndexAt(spans: MaVideoSpan[], t: number): number {
  let idx = 0;
  for (let i = 1; i < spans.length; i++) {
    if (t >= spans[i].startSeconds - 1e-6) idx = i;
    else break;
  }
  return idx;
}

/** 区間リストの同一判定（オプション切替の不要な再構築・再スケジュール防止用） */
export function videoSpansEqual(a: MaVideoSpan[], b: MaVideoSpan[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (span, i) =>
      span.videoItem.id === b[i].videoItem.id &&
      Math.abs(span.startSeconds - b[i].startSeconds) < 1e-6 &&
      Math.abs(span.fileOffsetSeconds - b[i].fileOffsetSeconds) < 1e-6 &&
      Math.abs((span.contentDurationSeconds ?? -1) - (b[i].contentDurationSeconds ?? -1)) < 1e-6,
  );
}

/** 動画 1 行 = 1 セグメント（= サイドバーの 1 サムネイル / 1 スライド） */
export interface MaSegment {
  /** シーケンス上の絶対開始秒（表示は 0 基準に正規化するため参考値） */
  sequenceStartSeconds: number;
  /**
   * 先頭区間（t=0）の表示動画（= videoSpans[0].videoItem）。
   * スライドの初期表示・サムネイル・書き出しファイル名・再マウントキーに使う
   */
  videoItem: MediaItem;
  /**
   * 本編行の動画（CSV 再生成と「動画なし候補へ戻したとき」の復元用。
   * オプションによる差し替えの影響を受けない）
   */
  baseVideoItem: MediaItem;
  /**
   * 本編行由来の動画区間（startSeconds 昇順・先頭は 0。オプション候補による
   * 差し替えの影響を受けない）。通常は 1 区間だが、RPP でソースオフセットが
   * 不連続な分割アイテム（ジャンプカット / リピート配置）は複数区間になる。
   * CSV ラウンドトリップ（区間ごとの動画行）と候補適用時の土台
   * （resolveVideoSpans の baseSpans）に使う。
   * 再生・書き出しのファイル時刻は videoSpans を使うこと
   */
  baseVideoSpans: MaVideoSpan[];
  /**
   * 動画表示区間（startSeconds 昇順・先頭は 0 開始）。
   * オプション候補の動画はスロット位置からの区間として入る
   */
  videoSpans: MaVideoSpan[];
  /**
   * 本編行由来の動画表示尺（秒）。end 指定があれば end - start、なければ
   * duration - offset。duration 未解析の場合は null（再生時は video 要素の
   * 実尺から解決）。オプション候補による延長の影響を受けない
   * （CSV 再生成の本編 end 列はこの値を使う）。
   * 再生・表示側は maSegmentDisplaySeconds() を使うこと
   */
  videoDisplaySeconds: number | null;
  /**
   * オプション候補適用後の表示尺（秒）。本編より長い候補動画が選択されて
   * いるとき、その候補のコンテンツ終端まで延長した値になる
   * （applyOptionSelectionsToSegment / 編集モードの再計算で設定）。
   * 未設定 = videoDisplaySeconds のまま
   */
  resolvedDisplaySeconds?: number;
  /** CSV で end が明示指定されていたか（CSV 再生成時に end 列を保持するため） */
  hasExplicitEnd: boolean;
  /** このセグメントで再生するオーディオクリップ（startSeconds 昇順） */
  clips: MaClip[];
  /**
   * クリップの最遅終了（セグメント相対秒）。動画尺を超える場合は
   * 動画停止後もオーディオを最後まで再生する（テール再生）
   */
  clipsEndSeconds: number;
  /** CSV 行番号 */
  line: number;
}

/**
 * セグメントの実効表示尺（秒）。オプション候補適用後の延長があればそれを、
 * なければ本編の表示尺を返す。再生・タイムライン・書き出しはこれを使う
 */
export function maSegmentDisplaySeconds(segment: MaSegment): number | null {
  return segment.resolvedDisplaySeconds ?? segment.videoDisplaySeconds;
}

export interface MaSequence {
  /** フレーム→秒換算に使うシーケンス fps（最初の動画の frame_rate、未解析は DEFAULT_FPS） */
  fps: number;
  /** 動画行順（start 昇順）のセグメント */
  segments: MaSegment[];
  warnings: MaWarning[];
  /**
   * CSV にリンクされたメディアアイテム ID の集合（リンクされたバリエーションの全テイク分）。
   * サイドバーのサムネイル非表示・編集リストの「MA」ラベル表示に使う
   */
  linkedItemIds: Set<string>;
  /** リンク済みオプションスロット（`(x)` / `[x]` マーカーなしのソースは空配列） */
  optionSlots: MaOptionSlot[];
  /**
   * 表示名 CSV 由来の「照合キー → 表示名」（parseMaDisplayNames の正規化キー）。
   * linkMaSequence は空 Map を返し、useMaSequence がショット内の表示名 CSV を
   * マージして上書きする。テロップ・タイムチャート CSV の label に使う
   */
  displayNames: Map<string, string>;
  /**
   * ルーラーレーン一覧（MaLinkOptions から伝搬。index 昇順）。
   * トラックの表示順・保存 CSV の `# lanes:` コメントに使う
   */
  rulerLanes?: MaRulerLane[];
  /** ビデオトラック名（MaLinkOptions から伝搬。タイムラインの MOV ラベル差し替え用） */
  videoTrackName?: string | null;
}

/** linkMaSequence の挙動オプション */
export interface MaLinkOptions {
  /**
   * 2 本目以降の動画で、最初のリージョン / マーカー / `(x)` スロット（分岐点）より
   * 前に始まるひとつ前の動画の音を引き継ぐ（バリエーション動画の共通部分の省略記法）。
   * 分岐点がなければ全て引き継ぐ。引き継ぎは連鎖する
   */
  inheritFromPreviousSegment: boolean;
  /**
   * ルーラーレーン一覧（パース結果の rulerLanes）。
   * - 名前付きトラックの採番をレーン index 順に事前登録し、タイムラインの
   *   トラック順を RPP のレーン順に揃える（未登場レーンはトラックを作らない）
   * - 個別色のない行のクリップ色をレーン色（トラック名一致）にフォールバック
   */
  rulerLanes?: MaRulerLane[];
  /** ビデオトラック名（パース結果の videoTrackName）。MaSequence へ伝搬される */
  videoTrackName?: string | null;
}

/** dB → リニアゲイン（-6dB ≒ 0.5） */
export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * クリップの実効長（秒）。トリム（durationSeconds）と後着優先カット
 * （truncateAtSeconds）を反映する。durationSeconds 未確定時は
 * デコード済みバッファの実尺などを fallbackDurationSeconds として渡す。
 * 再生エンジン（スケジュール）とレンダリング書き出しで共用。
 */
export function effectiveClipDurationSeconds(
  clip: Pick<MaClip, 'startSeconds' | 'durationSeconds' | 'truncateAtSeconds'>,
  fallbackDurationSeconds: number,
): number {
  const intrinsic = clip.durationSeconds ?? fallbackDurationSeconds;
  const maxDuration = (clip.truncateAtSeconds ?? Infinity) - clip.startSeconds;
  return Math.max(0, Math.min(intrinsic, maxDuration));
}

/** ファイル名から拡張子を除去（リンク照合・比較グループ検出・出力ファイル名で共用） */
export function stripExtension(name: string): string {
  return name.replace(/\.[^/.]+$/, '');
}

/**
 * メディア名照合用の正規化キー（NFC 正規化 + ゼロ幅文字除去 + 小文字化）。
 *
 * macOS でアップロードしたファイル名は濁点・半濁点が分解された NFD
 * （例: 「プ」=「フ」+ 結合濁点）になる一方、RPP / CSV 内の表記は
 * 通常 NFC のため、正規化せずに比較すると同じ見た目でも不一致になる。
 * また、コピー&ペースト由来のゼロ幅文字（ZWSP / ZWNJ / ZWJ / BOM。
 * 実例: 動画ファイル名に U+200C が混入）は見た目で区別できないため、
 * 片側だけに入っていても照合できるよう取り除く
 */
export function canonicalMediaKey(name: string): string {
  return name
    .normalize('NFC')
    .replace(/[\u200b-\u200d\ufeff]/g, '')
    .toLowerCase();
}

/** MaTimeValue を秒に変換（frames / .FF は fps で換算、seconds はそのまま） */
export function maTimeToSeconds(value: MaTimeValue, fps: number): number {
  if (value.kind === 'seconds') return value.seconds;
  const safeFps = fps > 0 ? fps : 30;
  if (value.kind === 'frames') return value.frames / safeFps;
  return value.minutes * 60 + value.seconds + (value.frames ?? 0) / safeFps;
}
